// Rechtstexte für die öffentliche Seite (agentos.arkturian.com).
//
// WICHTIG — wie das hier gemeint ist:
// Der INHALT beschreibt, was das System nachweislich tut (welche Daten entstehen,
// welche Anbieter sie sehen). Das ist recherchiert, nicht erfunden.
// Die ANGABEN ZUM ANBIETER kann niemand außer Alex liefern; sie stehen unten in
// LEGAL als Platzhalter und erscheinen im Text sichtbar als […], solange sie leer
// sind. Ausrollen = diese Konstanten ausfüllen.
//
// Und die eine Sache, die ein Agent nicht leisten kann: Das hier ist kein
// juristisch geprüfter Text. Er ist inhaltlich richtig, aber vor einer
// App-Store-Einreichung gehört er einmal von jemandem gelesen, der dafür haftet.

/** Angaben, die nur der Betreiber kennt. Leer = erscheint als sichtbarer Platzhalter. */
export const LEGAL = {
  /** Firma oder Name des Anbieters, z. B. "Arkturian GmbH" */
  entity: '',
  /** Ladungsfähige Anschrift, mehrzeilig */
  address: '',
  /** Allgemeine Kontaktadresse */
  email: '',
  /** Adresse für Datenschutzanfragen (oft dieselbe wie email) */
  privacyEmail: '',
  /** Vertretungsberechtigte Person, falls Gesellschaft */
  represented: '',
  /** Register + Nummer, falls eingetragen */
  register: '',
  /** USt-IdNr., falls vorhanden */
  vatId: '',
  /** Wo die Server tatsächlich stehen, z. B. "Deutschland (Linode) und Österreich" */
  serverLocations: '',
} as const;

/** Rendert einen Wert oder einen sichtbaren Platzhalter — nie eine erfundene Angabe. */
function V({ value, hint }: { value: string; hint: string }) {
  if (value) return <>{value}</>;
  return <span className="pub-placeholder">[{hint}]</span>;
}

/* ------------------------------------------------------------------ */

export function Privacy() {
  return (
    <main className="pub-main narrow">
      <p className="pub-eyebrow">Datenschutz</p>
      <h1>Datenschutzerklärung</h1>

      <h2>1. Verantwortlicher</h2>
      <p>
        <V value={LEGAL.entity} hint="Firma / Name einsetzen" />
        <br />
        <V value={LEGAL.address} hint="Anschrift einsetzen" />
        <br />
        E-Mail: <V value={LEGAL.privacyEmail || LEGAL.email} hint="Kontaktadresse einsetzen" />
      </p>

      <h2>2. Welche Daten verarbeitet werden</h2>

      <h3>Kontodaten</h3>
      <p>
        Bei der Registrierung werden E-Mail-Adresse und, falls angegeben, Vor- und
        Nachname gespeichert. Das Passwort wird ausschließlich als kryptografischer
        Hash abgelegt, nie im Klartext. Zu jedem Konto gehört eine technische Kennung
        sowie ein eigener Datenbereich (Tenant).
      </p>

      <h3>Inhaltsdaten</h3>
      <p>
        Alles, was in der Anwendung eingegeben wird, sowie die Antworten und
        Arbeitsergebnisse der Agenten: Nachrichten, Aufgaben, Dokumente, Notizen und
        hochgeladene Dateien. Diese Inhalte werden gespeichert, damit Verläufe
        erhalten bleiben und Agenten an früherer Arbeit anknüpfen können.
      </p>

      <h3>Sprachdaten</h3>
      <p>
        Wird die Spracheingabe genutzt, wird die Aufnahme zur Umwandlung in Text an
        einen Sprachdienst übertragen. Bei der Sprachausgabe wird der vorzulesende
        Text übertragen. In der Echtzeit-Sprachfunktion besteht während der Sitzung
        eine direkte Verbindung zwischen Browser und Sprachdienst.
      </p>

      <h3>Nutzungs- und Protokolldaten</h3>
      <p>
        Zeitpunkte von Anmeldungen und Aktivität, technische Protokolle der Server
        sowie Angaben zur Nutzung der Dienste. Diese Daten dienen dem Betrieb, der
        Fehlersuche und der Sicherheit.
      </p>

      <h3>Zahlungsdaten</h3>
      <p>
        Für kostenpflichtige Zugänge wird die Zahlungsabwicklung über Stripe
        durchgeführt. <strong>Kreditkartendaten werden nicht von uns erhoben oder
        gespeichert</strong> — sie werden unmittelbar bei Stripe eingegeben und dort
        verarbeitet. Wir erhalten von Stripe eine Kundenkennung sowie den Status des
        Abonnements.
      </p>

      <h2>3. Zwecke und Rechtsgrundlagen</h2>
      <table>
        <thead>
          <tr><th>Zweck</th><th>Rechtsgrundlage (DSGVO)</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Bereitstellung des Kontos und der Dienste</td>
            <td>Art. 6 Abs. 1 lit. b — Vertragserfüllung</td>
          </tr>
          <tr>
            <td>Verarbeitung der Inhalte durch KI-Dienste</td>
            <td>Art. 6 Abs. 1 lit. b — die Verarbeitung <em>ist</em> die Leistung</td>
          </tr>
          <tr>
            <td>Spracheingabe und -ausgabe</td>
            <td>Art. 6 Abs. 1 lit. a — Einwilligung durch aktive Nutzung der Funktion</td>
          </tr>
          <tr>
            <td>Zahlungsabwicklung</td>
            <td>Art. 6 Abs. 1 lit. b sowie lit. c — gesetzliche Aufbewahrung</td>
          </tr>
          <tr>
            <td>Betrieb, Sicherheit, Fehlersuche</td>
            <td>Art. 6 Abs. 1 lit. f — berechtigtes Interesse</td>
          </tr>
        </tbody>
      </table>

      <h2>4. Empfänger und Auftragsverarbeiter</h2>
      <p>
        <strong>Der wichtigste Punkt dieser Erklärung:</strong> Die Leistung besteht
        darin, Inhalte durch KI-Modelle verarbeiten zu lassen. Dabei werden die
        eingegebenen und erzeugten Inhalte an die Anbieter dieser Modelle übermittelt.
        Ohne diese Übermittlung gibt es keine Funktion.
      </p>
      <table>
        <thead>
          <tr><th>Empfänger</th><th>Wofür</th></tr>
        </thead>
        <tbody>
          <tr><td>Anthropic</td><td>Sprachmodelle für die Agenten</td></tr>
          <tr><td>OpenAI</td><td>Sprachmodelle, Spracherkennung, Sprachausgabe, Echtzeit-Sprache, Bildgenerierung</td></tr>
          <tr><td>Google</td><td>Sprachmodelle; Anmeldung mit Google-Konto, sofern genutzt</td></tr>
          <tr><td>MiniMax, DeepSeek</td><td>alternative Sprachmodelle, sofern ausgewählt</td></tr>
          <tr><td>Stripe</td><td>Zahlungsabwicklung für kostenpflichtige Zugänge</td></tr>
        </tbody>
      </table>
      <p>
        Welche Modelle im Einzelfall verwendet werden, hängt von der Konfiguration des
        jeweiligen Agenten ab.
      </p>

      <h2>5. Übermittlung in Drittländer</h2>
      <p>
        Die vorgenannten Anbieter haben ihren Sitz überwiegend in den Vereinigten
        Staaten. Eine Verarbeitung außerhalb der Europäischen Union findet daher statt.
        Grundlage sind die Standardvertragsklauseln der Europäischen Kommission
        beziehungsweise, soweit einschlägig, die Zertifizierung des Anbieters unter dem
        EU-US Data Privacy Framework.
      </p>

      <h2>6. Serverstandorte</h2>
      <p>
        Die Anwendung selbst wird betrieben in:{' '}
        <V value={LEGAL.serverLocations} hint="Serverstandorte einsetzen" />.
      </p>

      <h2>7. Speicherdauer</h2>
      <p>
        Konto- und Inhaltsdaten werden gespeichert, solange das Konto besteht. Nach
        Löschung des Kontos werden sie entfernt, soweit keine gesetzlichen
        Aufbewahrungspflichten entgegenstehen; das betrifft insbesondere
        Rechnungsdaten, die handels- und steuerrechtlich aufzubewahren sind.
        Protokolldaten werden turnusmäßig gelöscht.
      </p>
      <p>
        Auf die Speicherung bei den unter Punkt 4 genannten Anbietern haben wir nur
        mittelbar Einfluss; es gelten deren Aufbewahrungs- und Löschfristen.
      </p>

      <h2>8. Deine Rechte</h2>
      <p>
        Es bestehen die Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung der
        Verarbeitung, Datenübertragbarkeit sowie Widerspruch gegen Verarbeitungen, die
        auf einem berechtigten Interesse beruhen. Eine erteilte Einwilligung kann
        jederzeit mit Wirkung für die Zukunft widerrufen werden.
      </p>
      <p>
        Anfragen an: <V value={LEGAL.privacyEmail || LEGAL.email} hint="Kontaktadresse einsetzen" />
      </p>
      <p>
        Unabhängig davon besteht ein Beschwerderecht bei einer
        Datenschutz-Aufsichtsbehörde.
      </p>

      <h2>9. Keine automatisierte Entscheidung mit Rechtswirkung</h2>
      <p>
        Die Agenten erzeugen Vorschläge und Arbeitsergebnisse. Es findet keine
        automatisierte Entscheidungsfindung statt, die gegenüber Nutzern rechtliche
        Wirkung entfaltet oder sie in ähnlicher Weise erheblich beeinträchtigt.
      </p>

      <h2>10. Änderungen</h2>
      <p>
        Diese Erklärung wird angepasst, wenn sich die beschriebenen Verarbeitungen
        ändern — etwa wenn ein weiterer Modellanbieter hinzukommt.
      </p>
    </main>
  );
}

/* ------------------------------------------------------------------ */

export function Impressum() {
  return (
    <main className="pub-main narrow">
      <p className="pub-eyebrow">Impressum</p>
      <h1>Anbieterkennzeichnung</h1>

      <h2>Anbieter</h2>
      <p>
        <V value={LEGAL.entity} hint="Firma / Name einsetzen" />
        <br />
        <V value={LEGAL.address} hint="Anschrift einsetzen" />
      </p>

      <h2>Kontakt</h2>
      <p>
        E-Mail: <V value={LEGAL.email} hint="Kontaktadresse einsetzen" />
      </p>

      <h2>Vertretung und Register</h2>
      <p>
        Vertreten durch: <V value={LEGAL.represented} hint="entfällt, falls Einzelunternehmen" />
        <br />
        Register: <V value={LEGAL.register} hint="entfällt, falls nicht eingetragen" />
        <br />
        Umsatzsteuer-Identifikationsnummer: <V value={LEGAL.vatId} hint="entfällt, falls keine" />
      </p>

      <h2>Verantwortlich für den Inhalt</h2>
      <p>
        <V value={LEGAL.represented || LEGAL.entity} hint="Name einsetzen" />
      </p>
    </main>
  );
}

/* ------------------------------------------------------------------ */

export function Support() {
  return (
    <main className="pub-main narrow">
      <p className="pub-eyebrow">Support</p>
      <h1>Hilfe und Kontakt</h1>

      <p className="pub-lede">
        Bei Fragen zum Zugang, zur Anmeldung oder zu einem Abonnement erreichst du uns
        per E-Mail.
      </p>

      <p>
        <strong>
          <V value={LEGAL.email} hint="Support-Adresse einsetzen" />
        </strong>
      </p>

      <h2>Womit wir helfen</h2>
      <ul>
        <li>Fragen zum beantragten Zugang und zur Freischaltung</li>
        <li>Probleme bei der Anmeldung oder der E-Mail-Bestätigung</li>
        <li>Fragen zu Abonnement und Abrechnung</li>
        <li>Fehler in der Weboberfläche oder der iOS-App</li>
        <li>Auskunft, Berichtigung und Löschung nach Datenschutzrecht</li>
      </ul>

      <h2>Bestehende Nutzer</h2>
      <p>
        Wer bereits einen Zugang hat, erreicht die Weboberfläche unverändert unter{' '}
        <a href="https://cloud2.arkturian.com" target="_blank" rel="noopener noreferrer">
          cloud2.arkturian.com
        </a>
        .
      </p>
    </main>
  );
}
